import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequestWithRetry } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type {
	OnboardingPersonalisationResponse,
	FetchOnboardingPersonalisationArgs,
	ExperienceType,
} from './types.tsx';
import {
	getLocalStorageOnboardingPersonalisationResponse,
	setLocalStorageOnboardingPersonalisationResponse,
} from './utils.tsx';

const PERSONALISATION_ENDPOINT = '/rest/personalisation/v1/experience';
const TRACK_PERSONALISATION_ENDPOINT = '/rest/personalisation/v1/track';

const responseHasAllRequestedExperienceTypes = ({
	response,
	requestedExperienceTypes,
}: {
	response: OnboardingPersonalisationResponse;
	requestedExperienceTypes: ExperienceType[];
}) =>
	requestedExperienceTypes.every(
		(requestedExperienceType) => response.experience[requestedExperienceType],
	);

export const fetchOnboardingPersonalisationWithRethrow = async ({
	experienceTypes = ['QUICKSTART', 'TEMPLATE', 'LANDING_PAGE'],
	additionalParameters,
}: FetchOnboardingPersonalisationArgs): Promise<OnboardingPersonalisationResponse> => {
	try {
		const localStorageOnboardingPersonalisationResponse =
			getLocalStorageOnboardingPersonalisationResponse();

		if (
			localStorageOnboardingPersonalisationResponse &&
			responseHasAllRequestedExperienceTypes({
				response: localStorageOnboardingPersonalisationResponse,
				requestedExperienceTypes: experienceTypes,
			})
		) {
			return localStorageOnboardingPersonalisationResponse;
		}

		const response = await performPostRequestWithRetry<
			OnboardingPersonalisationResponse | undefined
		>(PERSONALISATION_ENDPOINT, {
			body: JSON.stringify({
				experienceTypes,
				additionalParameters,
			}),
		});

		if (response) {
			const mergedResponse = {
				experience: {
					...localStorageOnboardingPersonalisationResponse?.experience,
					...response.experience,
				},
				matched: response.matched,
			};

			setLocalStorageOnboardingPersonalisationResponse(mergedResponse);
			return mergedResponse;
		}

		throw new Error('response from OnboardingPersonalisation is falsy');

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			error,
			meta: {
				id: 'fetchOnboardingPersonalisationWithRethrow',
				packageName: 'jiraOnboardingCore',
				teamName: 'jlove-makkuro',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
		throw error;
	}
};

export const trackPersonalisation = ({
	additionalParameters,
}: FetchOnboardingPersonalisationArgs): void => {
	try {
		performPostRequestWithRetry<OnboardingPersonalisationResponse | undefined>(
			TRACK_PERSONALISATION_ENDPOINT,
			{
				body: JSON.stringify({
					additionalParameters,
				}),
			},
		);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			error,
			meta: {
				id: 'trackOnboardingPersonalisation',
				packageName: 'jiraOnboardingCore',
				teamName: 'jlove-makkuro',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	}
};
